import React, { useMemo, useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import EmptyState from '../../shared/components/EmptyState';
import SlideOver from '../../shared/components/SlideOver';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';
import { date } from '../../shared/formatters';

const STATUS_OPTIONS = [
  ['all', 'All'],
  ['draft', 'Draft'],
  ['in_progress', 'In progress'],
  ['completed', 'Completed'],
  ['submitted', 'Submitted'],
];

const STATUS_COLOR = {
  draft: 'gray',
  in_progress: 'blue',
  completed: 'green',
  submitted: 'purple',
};

const STATUS_LABEL = {
  draft: 'Draft',
  in_progress: 'In progress',
  completed: 'Completed',
  submitted: 'Submitted',
};

// Render a form_data value readably (objects/arrays → joined text).
function renderValue(value) {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// Admin review queue for engineer job sheets (update_tasks.md §7.1).
// Read-only except admin_notes; no status flips or destructive actions
// until the TBC-1 decision adds an admin-approved state.
export default function JobSheets() {
  const { data, update } = useData();
  const { notify } = useToast();
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const sheets = useMemo(() => data.engineerJobSheets.filter((sheet) => {
    if (status !== 'all' && sheet.status !== status) return false;
    if (!query) return true;
    const engineerJobTitle = sheet.engineer_job?.title || '';
    const jobTitle = data.jobs.find((job) => job.id === sheet.engineer_job?.job_id)?.title || '';
    const haystack = `${sheet.client} ${engineerJobTitle} ${jobTitle} ${sheet.work_summary} ${sheet.admin_notes}`.toLowerCase();
    return haystack.includes(query.toLowerCase());
  }), [data, status, query]);

  const openSheet = (sheet) => {
    setSelected(sheet);
    setNotes(sheet.admin_notes || '');
  };

  const saveNotes = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await update('engineerJobSheets', selected.id, { admin_notes: notes });
      notify('Review notes saved');
    } catch (err) {
      notify(err.message || 'Could not save notes', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Re-open a completed/submitted sheet so the engineer can amend it. The
  // captured signature row is untouched (engineers have no update rule on
  // engineer_signatures); re-completion appends a NEW signature record, so
  // the original signed record is preserved as an audit trail.
  const reopenSheet = async () => {
    setSaving(true);
    try {
      const ui = await update('engineerJobSheets', selected.id, { status: 'in_progress', completed_at: '' });
      setSelected(ui);
      notify('Sheet re-opened — engineer can amend it now');
    } catch (err) {
      notify(err.message || 'Could not re-open the sheet', 'error');
    } finally {
      setSaving(false);
    }
  };

  const formEntries = selected ? Object.entries(selected.form_data || {}) : [];
  const jobPhotos = selected?.form_data?.photos || selected?.photos || [];

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Engineer</span>
          <h1>Job Sheets</h1>
          <p className="field-hint">
            {sheets.length} sheet{sheets.length === 1 ? '' : 's'} — review work summaries, materials and photos. Add notes; sheets are read-only until the approved-state decision lands.
          </p>
        </div>
      </header>

      <div className="toolbar">
        <label className="search-input">
          <SafeIcon icon={Icons.Search} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search client, job or summary…" />
        </label>
        <div className="filter-chips" role="group" aria-label="Filter by status">
          {STATUS_OPTIONS.map(([value, label]) => (
            <button key={value} className={`chip ${status === value ? 'chip-active' : ''}`} onClick={() => setStatus(value)}>{label}</button>
          ))}
        </div>
      </div>

      {!data.engineerJobSheets.length ? (
        <EmptyState title="No job sheets yet" message="Sheets engineers complete will appear here for review." />
      ) : !sheets.length ? (
        <EmptyState title="No matching sheets" message="Try a different status or search." />
      ) : (
        <div className="job-sheets-grid">
          {sheets.map((sheet) => {
            const linkedJob = data.jobs.find((job) => job.id === sheet.engineer_job?.job_id);
            return (
              <button type="button" className="job-sheet-card" key={sheet.id} onClick={() => openSheet(sheet)}>
                <div className="card-top">
                  <span className={`sheet-status status-${STATUS_COLOR[sheet.status] || 'gray'}`}>{STATUS_LABEL[sheet.status] || sheet.status}</span>
                </div>
                <strong>{sheet.client || 'Unknown client'}</strong>
                <span className="field-hint">{sheet.engineer_job?.title || linkedJob?.title || 'Untitled job'}</span>
                {sheet.work_summary ? <p className="sheet-summary">{sheet.work_summary.slice(0, 140)}{sheet.work_summary.length > 140 ? '…' : ''}</p> : <p className="field-hint">No summary recorded.</p>}
                {sheet.materials_used ? <p className="sheet-materials">{sheet.materials_used}</p> : null}
                <small>{date(sheet.updated || sheet.created)} · {sheet.status === 'submitted' && sheet.submitted_at ? `submitted ${date(sheet.submitted_at)}` : `updated ${date(sheet.updated || sheet.created)}`}</small>
              </button>
            );
          })}
        </div>
      )}

      <SlideOver open={Boolean(selected)} title="Job sheet review" onClose={() => setSelected(null)}>
        {selected && (
          <div className="drawer-form">
            <div className="panel">
              <div className="panel-head">
                <span className="eyebrow">{selected.client || 'Unknown client'}</span>
                <h3>{selected.engineer_job?.title || 'Job sheet'}</h3>
                <span className={`sheet-status status-${STATUS_COLOR[selected.status] || 'gray'}`}>{STATUS_LABEL[selected.status] || selected.status}</span>
              </div>

              {selected.work_summary ? (
                <div className="detail-panel"><div className="section-header"><h4>Work summary</h4></div><p>{selected.work_summary}</p></div>
              ) : null}
              {selected.materials_used ? (
                <div className="detail-panel"><div className="section-header"><h4>Materials used</h4></div><p>{selected.materials_used}</p></div>
              ) : null}
              {selected.engineer_notes ? (
                <div className="detail-panel"><div className="section-header"><h4>Engineer notes</h4></div><p>{selected.engineer_notes}</p></div>
              ) : null}

              {formEntries.some(([key, value]) => key !== 'photos' && key !== 'checklist' && value != null && value !== '' && !(Array.isArray(value) && !value.length)) && (
                <div className="detail-panel">
                  <div className="section-header"><h4>Form details</h4></div>
                  <dl className="spec-table">
                    {formEntries.filter(([key, value]) => key !== 'photos' && key !== 'checklist' && value != null && value !== '' && !(Array.isArray(value) && !value.length)).map(([key, value]) => (
                      <div key={key}><dt>{key.replace(/_/g, ' ')}</dt><dd>{renderValue(value)}</dd></div>
                    ))}
                  </dl>
                </div>
              )}

              {Array.isArray(selected.form_data?.checklist) && selected.form_data.checklist.length > 0 && (
                <div className="detail-panel">
                  <div className="section-header"><h4>Checklist</h4></div>
                  <ul className="fault-steps">
                    {selected.form_data.checklist.map((item, index) => (
                      <li key={index}>{item.done ? '✓' : '○'} {item.label || item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {Array.isArray(jobPhotos) && jobPhotos.length > 0 && (
                <div className="detail-panel">
                  <div className="section-header"><h4>Photos ({jobPhotos.length})</h4></div>
                  <div className="photo-summary">
                    {jobPhotos.map((photo, index) => (
                      <img key={index} src={photo} alt={`Job photo ${index + 1}`} className="job-photo-thumb" />
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-panel">
                <div className="section-header"><h4>Timeline</h4></div>
                <dl className="spec-table">
                  <div><dt>Started</dt><dd>{selected.started_at ? date(selected.started_at) : '—'}</dd></div>
                  <div><dt>Completed</dt><dd>{selected.completed_at ? date(selected.completed_at) : '—'}</dd></div>
                  <div><dt>Submitted</dt><dd>{selected.submitted_at ? date(selected.submitted_at) : '—'}</dd></div>
                </dl>
              </div>

              <form onSubmit={saveNotes}>
                <label>Admin review notes
                  <textarea rows="4" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Notes for the team — visible to agency only." />
                </label>
                <div className="drawer-actions">
                  <button type="submit" className="primary-button" disabled={saving}>{saving ? 'Saving…' : 'Save notes'}</button>
                  {(selected.status === 'completed' || selected.status === 'submitted') && (
                    <button type="button" className="secondary-button" onClick={reopenSheet} disabled={saving}>
                      <SafeIcon icon={Icons.RefreshCw} size={14} />
                      Reopen for amendments
                    </button>
                  )}
                </div>
                <p className="field-hint">
                  Reopening lets the engineer amend the sheet and capture a fresh signature on
                  re-completion. The original signature record is always preserved.
                </p>
              </form>
            </div>
          </div>
        )}
      </SlideOver>
    </div>
  );
}
